/**
 * CustomerListTable - Kundenliste mit Filter und Suche
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Eye, FileText, AlertCircle } from 'lucide-react';

const CustomerListTable = ({
  customers,
  filteredCustomers,
  loading,
  searchTerm,
  setSearchTerm,
  activeFilter,
  setActiveFilter,
  onViewCustomer,
  anamneseStatus = {},
  onViewAnamnese
}) => {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <span className="ml-3">Lade Kunden...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        {/* Filter Buttons */}
        <div className="flex items-center gap-2 mb-4">
          <Button
            variant={activeFilter === 'active' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('active')}
            data-testid="filter-active"
          >
            Aktiv
          </Button>
          <Button
            variant={activeFilter === 'inactive' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('inactive')}
            data-testid="filter-inactive"
          >
            Inaktiv
          </Button>
          <Button
            variant={activeFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveFilter('all')}
            data-testid="filter-all"
          >
            Alle
          </Button>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              type="text"
              placeholder="Suche nach Name, E-Mail oder Kundennr. (z.B. 10042)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="customer-search-input"
            />
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kundennr.</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>E-Mail</TableHead>
              <TableHead>Telefon</TableHead>
              <TableHead>Abo</TableHead>
              <TableHead>Termine</TableHead>
              <TableHead>Anamnese</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Registriert</TableHead>
              <TableHead>Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCustomers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                  {searchTerm ? 'Keine Kunden gefunden' : 'Keine Kunden vorhanden'}
                </TableCell>
              </TableRow>
            ) : (
              filteredCustomers.map((customer, index) => {
                const hasAnamnese = anamneseStatus[customer.id];
                return (
                <TableRow
                  key={customer.id}
                  data-testid={`customer-row-${index}`}
                  onClick={() => onViewCustomer(customer)}
                  className="cursor-pointer hover:bg-gray-50"
                >
                  <TableCell className="font-mono text-sm text-gray-700" data-testid={`customer-number-${index}`}>
                    {customer.customer_number || '-'}
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2.5">
                      <div className="h-8 w-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold shrink-0 overflow-hidden" data-testid={`customer-avatar-${index}`}>
                        {customer.photo_url
                          ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api${customer.photo_url}`} alt="" className="h-full w-full object-cover" />
                          : `${(customer.first_name || '?')[0] || ''}${(customer.last_name || '')[0] || ''}`.toUpperCase()}
                      </div>
                      <span>{customer.first_name} {customer.last_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{customer.email}</TableCell>
                  <TableCell>{customer.phone || '-'}</TableCell>
                  <TableCell>
                    {customer.has_subscription ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Abo
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                        Kein Abo
                      </span>
                    )}
                  </TableCell>
                  <TableCell data-testid={`customer-appointment-count-${index}`}>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      (customer.appointment_count || 0) > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {customer.appointment_count || 0}
                    </span>
                  </TableCell>
                  <TableCell>
                    {hasAnamnese ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-green-600 hover:text-green-700 hover:bg-green-50 p-1 h-auto"
                        onClick={(e) => { e.stopPropagation(); onViewAnamnese && onViewAnamnese(customer); }}
                        title="Anamnese ansehen"
                        data-testid={`customer-anamnese-view-${index}`}
                      >
                        <FileText size={18} />
                      </Button>
                    ) : (
                      <span className="flex items-center gap-1 text-red-500" title="Keine Anamnese vorhanden" data-testid={`customer-anamnese-missing-${index}`}>
                        <AlertCircle size={18} />
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      customer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {customer.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {new Date(customer.created_at || Date.now()).toLocaleDateString('de-DE')}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onViewCustomer(customer); }}
                      data-testid={`view-customer-${index}`}
                    >
                      <Eye size={14} className="mr-1" />
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              )})
            )}
          </TableBody>
        </Table>

        {filteredCustomers.length > 0 && (
          <div className="mt-4 text-sm text-gray-600">
            {filteredCustomers.length} von {customers.length} Kunden
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerListTable;
